<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import { Bell } from 'lucide-vue-next';
import { onMounted, ref } from 'vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useI18n } from '@/composables/useI18n';
import { useNotifications } from '@/composables/useNotifications';
import type { AppNotificationItem } from '@/types/global';

const { __ } = useI18n();
const {
    unreadCount,
    items,
    refresh,
    titleFor,
    messageFor,
    openNotification,
    markAllRead,
} = useNotifications();

const page = usePage();
const seeded = ref(false);

// Seed the store from the shared page props (updates on every navigation).
onMounted(() => {
    if (seeded.value) {
        return;
    }

    const props = page.props.notifications;

    if (props) {
        unreadCount.value = props.unread_count;
        items.value = props.items;
    }

    seeded.value = true;
    void refresh();
});
</script>

<template>
    <DropdownMenu>
        <DropdownMenuTrigger as-child>
            <Button
                variant="ghost"
                size="icon"
                class="relative"
                :aria-label="__('notifications.aria_label')"
            >
                <Bell class="size-5" />
                <Badge
                    v-if="unreadCount > 0"
                    variant="destructive"
                    class="absolute -top-1 -right-1 size-5 justify-center px-0 text-[10px]"
                >
                    {{ unreadCount > 99 ? '99+' : unreadCount }}
                </Badge>
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent class="w-80" align="end" :side-offset="4">
            <div class="flex items-center justify-between px-3 py-2">
                <p class="text-sm font-semibold text-foreground">
                    {{ __('notifications.title') }}
                </p>
                <div class="flex items-center gap-1">
                    <Button
                        as-child
                        variant="ghost"
                        size="sm"
                        class="h-7 px-2 text-xs"
                    >
                        <a :href="'/notifications'">
                            {{ __('notifications.view_all') }}
                        </a>
                    </Button>
                    <Button
                        v-if="items.length > 0"
                        variant="ghost"
                        size="sm"
                        class="h-7 px-2 text-xs"
                        @click="markAllRead"
                    >
                        {{ __('notifications.mark_all_read') }}
                    </Button>
                </div>
            </div>
            <div class="max-h-96 overflow-y-auto border-t border-border">
                <template v-if="items.length > 0">
                    <button
                        v-for="notification in items as AppNotificationItem[]"
                        :key="notification.id"
                        type="button"
                        class="flex w-full flex-col gap-0.5 border-b border-border/60 px-3 py-3 text-left transition-colors hover:bg-accent"
                        @click="openNotification(notification)"
                    >
                        <span class="text-sm font-medium text-foreground">
                            {{ titleFor(notification.type, notification.data) }}
                        </span>
                        <span
                            v-if="
                                messageFor(notification.type, notification.data)
                            "
                            class="text-xs text-muted-foreground"
                        >
                            {{
                                messageFor(notification.type, notification.data)
                            }}
                        </span>
                        <span class="text-[11px] text-muted-foreground/70">
                            {{ notification.created_at }}
                        </span>
                    </button>
                </template>
                <p
                    v-else
                    class="px-3 py-6 text-center text-sm text-muted-foreground"
                >
                    {{ __('notifications.empty') }}
                </p>
            </div>
        </DropdownMenuContent>
    </DropdownMenu>
</template>
